import React, { useEffect, useRef, useState } from "react";
import { api } from "../lib/api";
import { Loader2, ListMusic, Play, Shuffle } from "lucide-react";

// Loads the YouTube IFrame Player API once and reports when it's ready.
function useYouTubeApi() {
  const [ready, setReady] = useState(!!(window.YT && window.YT.Player));
  useEffect(() => {
    if (window.YT && window.YT.Player) { setReady(true); return; }
    if (!document.getElementById("yt-iframe-api")) {
      const s = document.createElement("script");
      s.id = "yt-iframe-api";
      s.src = "https://www.youtube.com/iframe_api";
      document.body.appendChild(s);
    }
    const t = setInterval(() => { if (window.YT && window.YT.Player) { setReady(true); clearInterval(t); } }, 300);
    return () => clearInterval(t);
  }, []);
  return ready;
}

export const ProfilePlaylist = ({ playlistId }) => {
  const [videos, setVideos] = useState(null);
  const [index, setIndex] = useState(0);
  const [autoplay, setAutoplay] = useState(true);
  const playerRef = useRef(null);
  const holderRef = useRef(null);
  const apiReady = useYouTubeApi();
  const autoplayRef = useRef(true);
  const videosRef = useRef([]);
  const errorCountRef = useRef(0);
  const readyRef = useRef(false);

  useEffect(() => { autoplayRef.current = autoplay; }, [autoplay]);

  useEffect(() => {
    const url = playlistId ? `/youtube/playlist?playlist_id=${encodeURIComponent(playlistId)}` : "/youtube/playlist";
    api.get(url)
      .then(({ data }) => { const v = data.videos || []; videosRef.current = v; setVideos(v); if (v.length) setIndex(Math.floor(Math.random() * v.length)); })
      .catch(() => setVideos([]));
  }, [playlistId]);

  const active = videos && videos.length ? videos[index] : null;

  // Shuffle: pick a random track different from the current one.
  const advance = () => setIndex((prev) => {
    const n = videosRef.current.length;
    if (n <= 1) return prev;
    let r = Math.floor(Math.random() * n);
    if (r === prev) r = (r + 1) % n;
    return r;
  });

  // Create the player once the API + first video are ready (cued, not auto-played).
  useEffect(() => {
    if (!apiReady || !active || !holderRef.current || playerRef.current) return;
    playerRef.current = new window.YT.Player(holderRef.current, {
      width: "100%", height: "100%", videoId: active.videoId,
      playerVars: { rel: 0, playsinline: 1 },
      events: {
        onReady: (e) => { readyRef.current = true; if (autoplayRef.current) { try { e.target.playVideo(); } catch (err) {} } },
        onStateChange: (e) => {
          if (e.data === window.YT.PlayerState.PLAYING) errorCountRef.current = 0;
          if (e.data === window.YT.PlayerState.ENDED && autoplayRef.current) advance();
        },
        onError: () => {
          // shuffle past embed-restricted / unavailable videos
          if (autoplayRef.current && errorCountRef.current < videosRef.current.length) {
            errorCountRef.current += 1;
            advance();
          }
        },
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiReady, active]);

  useEffect(() => () => { try { playerRef.current?.destroy?.(); } catch (e) {} playerRef.current = null; }, []);

  // Load & play whenever the selected track changes (user pick or shuffle advance), once the player is ready.
  useEffect(() => {
    const p = playerRef.current;
    if (p && readyRef.current && p.loadVideoById && active) p.loadVideoById(active.videoId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [index]);

  if (videos === null) {
    return (
      <div className="bg-white border-4 border-black rounded-2xl mt-6 p-4 brand-shadow flex justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-spotter-maroon" />
      </div>
    );
  }
  if (videos.length === 0) return null;

  return (
    <div className="bg-white border-4 border-black rounded-2xl mt-6 p-4 brand-shadow" data-testid="profile-playlist">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <h3 className="font-display text-2xl font-bold text-spotter-maroon flex items-center gap-2"><ListMusic className="w-6 h-6" style={{ color: "#E19C00" }} />My Playlist<span className="flex items-center gap-1 text-xs font-heading font-semibold text-spotter-maroon/70"><Shuffle className="w-3.5 h-3.5" />Shuffle</span></h3>
        <button data-testid="autoplay-toggle" onClick={() => setAutoplay((a) => !a)}
          className={`flex items-center gap-2 text-xs font-bold border-2 border-black rounded-full pl-2 pr-1 py-1 transition-colors ${autoplay ? "bg-spotter-maroon text-spotter-cream" : "bg-white text-spotter-maroon"}`}>
          Autoplay {autoplay ? "On" : "Off"}
          <span className={`w-8 h-4 rounded-full border-2 border-black flex items-center ${autoplay ? "bg-spotter-gold justify-end" : "bg-spotter-cream justify-start"}`}>
            <span className="w-3 h-3 rounded-full bg-black block m-0.5" />
          </span>
        </button>
      </div>
      <div className="grid sm:grid-cols-2 gap-4 items-start">
        {/* Player */}
        <div>
          <div className="relative w-full rounded-xl overflow-hidden border-2 border-black bg-black" style={{ paddingTop: "56.25%" }}>
            <div className="absolute inset-0" data-testid="profile-playlist-player"><div ref={holderRef} className="w-full h-full" /></div>
          </div>
          {active && (
            <div className="mt-2">
              <div className="font-heading font-bold text-sm text-spotter-maroon truncate" data-testid="profile-now-playing">{active.title}</div>
              <a href={`https://www.youtube.com/watch?v=${active.videoId}`} target="_blank" rel="noopener noreferrer" className="text-xs text-spotter-maroon underline hover:no-underline">Won't play? Watch on YouTube →</a>
            </div>
          )}
        </div>
        {/* Track list */}
        <div className="border-2 border-black rounded-xl overflow-hidden">
          <div className="max-h-64 overflow-y-auto">
            {videos.map((v, i) => {
              const isActive = index === i;
              return (
                <button key={v.videoId} data-testid={`profile-track-${v.videoId}`} onClick={() => setIndex(i)}
                  className={`w-full flex items-center gap-2 p-2 border-b border-black/10 text-left transition-colors ${isActive ? "bg-spotter-gold/25" : "hover:bg-spotter-cream"}`}>
                  <span className="w-4 text-center text-xs font-bold text-spotter-maroon shrink-0">{i + 1}</span>
                  <div className="relative w-12 shrink-0 rounded overflow-hidden border border-black">
                    {v.thumbnail ? <img src={v.thumbnail} className="w-full aspect-video object-cover" alt="" /> : <div className="w-full aspect-video bg-spotter-gold/30" />}
                    {isActive && <span className="absolute inset-0 flex items-center justify-center bg-black/40"><Play className="w-3 h-3 text-white fill-white" /></span>}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-xs font-semibold truncate">{v.title}</div>
                    {v.duration && <div className="text-[10px] text-spotter-maroon/70">{v.duration}</div>}
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};
